import { FC, useMemo } from 'react';
import { styled } from '@mui/material';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { LockedLockIcon, MessageIcon } from 'assets/icons';
import {
  creatorProfileRollSelector,
  creatorProfileSelector,
} from '../store/creators.selectors';
import {
  MessageBadgeLeft,
  MessageBadgeRight,
} from 'components/styled/message-badges';
import { newMessagesCountSelector } from 'app/messages/store/messages.selectors';
import { countSubscriptionDaysRemaining } from 'handlers/count-subscription-days-remaining';
import { NavigateMessagesHandlerProps } from 'app/messages/types/navigate-messages-handler.props';

import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Avatar from '@mui/material/Avatar';
import Grid from '@mui/material/Grid';
import MarqueeTextBar from './marquee-text-bar';
import useResponsiveText from 'hooks/use-responsive-text';
import { colors } from 'constants/colors';

const BackgroundAvatar = styled(Avatar)({
  width: '100%',
  height: '500px',
  position: 'absolute',
  top: 0,
  zIndex: 0,
});

interface CreatorProfileInfoMobileProps {
  isToShowSubscribedUI: boolean;
  isToShowUnsubscribedUI: boolean;
  isConversationEnabled: boolean;
  isPublic?: boolean;
  handleOpenSubscribe: () => void;
  navigateMessageCreator: ({
    conversationId,
    creatorId,
  }: NavigateMessagesHandlerProps) => void;
}
const CreatorProfileInfoMobile: FC<CreatorProfileInfoMobileProps> = ({
  handleOpenSubscribe,
  isToShowUnsubscribedUI,
  isToShowSubscribedUI,
  isConversationEnabled,
  navigateMessageCreator,
}) => {
  const { t } = useTranslation();

  const profile = useSelector(creatorProfileSelector);
  const roll = useSelector(creatorProfileRollSelector);
  const newMessagesCounter = useSelector(newMessagesCountSelector);

  const daysRemaining = useMemo(() => {
    if (!profile?.subscription || !profile.subscription.periodEnd) {
      return 0;
    }
    return countSubscriptionDaysRemaining(profile.subscription.periodEnd);
  }, [profile]);

  const { fontSize, textRef } = useResponsiveText<HTMLDivElement>(46);

  if (!profile) {
    return <></>;
  }

  return (
    <>
      <Grid
        component="section"
        container
        sx={{ width: '100%', display: { md: 'none' } }}
      >
        <BackgroundAvatar
          variant="square"
          src={roll?.creatorAvatar?.assets[0]?.assetUrl}
        />

        <div
          style={{
            background:
              'linear-gradient(0deg, rgba(239, 153, 233, 0.9) 20%, rgba(239, 153, 233, 0) 40%)',
            width: '100%',
            height: '500px',
            position: 'absolute',
            left: 0,
            zIndex: 0,
            top: 0,
          }}
        />

        <Grid
          container
          sx={{
            height: '500px',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'flex-end',
            alignItems: 'center',
            zIndex: 1,
          }}
        >
          <Grid item ref={textRef}>
            <Typography
              className="Modak color-white"
              variant="h3"
              component="h1"
              textTransform="uppercase"
              sx={{ fontSize: fontSize }}
            >
              {profile.username}
            </Typography>
          </Grid>

          {isToShowSubscribedUI && isConversationEnabled && (
            <Grid item>
              <Button
                variant="contained"
                color="green"
                size="small"
                startIcon={
                  <Avatar
                    src={roll?.creatorAvatar?.assets[0]?.assetUrl}
                    sx={{
                      position: 'absolute',
                      top: '3px',
                      width: '53px',
                      height: '53px',
                      left: '-49px',
                      border: '2px solid #ACFB68',
                    }}
                  />
                }
                endIcon={<MessageIcon />}
                onClick={() =>
                  navigateMessageCreator({
                    conversationId: profile.conversation.id,
                    creatorId: profile.id,
                  })
                }
                sx={{
                  position: 'relative',
                  flex: '0 0',
                  width: 'fit-content',
                  marginLeft: '49px',
                  '&:before': {
                    display: 'block',
                    width: '20px',
                    height: '28px',
                    background: '#ACFB68',
                    position: 'absolute',
                    top: '16px',
                    left: '-10px',
                    content: '" "',
                  },
                }}
              >
                {t('buttons.private-messages')}
                <MessageBadgeRight
                  badgeContent={newMessagesCounter}
                  color="error"
                />
              </Button>
            </Grid>
          )}

          {isToShowUnsubscribedUI && (
            <Grid
              item
              sx={{
                gap: '4px',
                display: 'flex',
                justifyContent: 'space-between',
              }}
            >
              <Button
                variant="contained"
                color="green"
                size="small"
                startIcon={<LockedLockIcon />}
                onClick={handleOpenSubscribe}
              >
                {t(
                  profile?.subscription?.periodEnd
                    ? 'buttons.resubscribe'
                    : 'buttons.unlock-roll'
                )}
              </Button>
              {isConversationEnabled && (
                <Button
                  variant="contained"
                  color="green"
                  size="small"
                  startIcon={<MessageIcon />}
                  onClick={() =>
                    navigateMessageCreator({
                      conversationId: profile.conversation.id,
                      creatorId: profile.id,
                    })
                  }
                >
                  {t('buttons.message-me')}
                  <MessageBadgeLeft
                    color="error"
                    badgeContent={newMessagesCounter}
                    style={{ left: '10px' }}
                  />
                </Button>
              )}
            </Grid>
          )}

          <Grid item sx={{ padding: '19px 22px' }}>
            <Typography
              variant="body2"
              component="p"
              sx={{
                fontFamily: 'Avenir-Regular',
                color: colors.default.infoText,
                fontSize: '14px',
                wordBreak: 'break-word',
              }}
            >
              {profile.bio}
            </Typography>
          </Grid>
        </Grid>
      </Grid>
      {profile?.subscription?.periodEnd && (
        <MarqueeTextBar
          pinkText={`${daysRemaining} DAYS`}
          text="REMAINING IN SUBSCRIPTION"
          icon
        />
      )}
    </>
  );
};
export default CreatorProfileInfoMobile;
